import { useState, useEffect, useCallback, useRef } from 'react'
import type { FormData } from '@/types'
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom'
import { FormWrapper } from '@components/Form/FormWrapper'
import { useSession } from '@hooks/useSession'
import { useFormActions } from '@hooks/useFormStore'
import { clearIdentityDocumentsServerState } from '@services/api'
import { LoadingScreen, SessionTimeoutWarning, OperatorNavbar, FormSkeleton } from '@components/UI'
import { ClientSigningWrapper } from '@components/ClientSigning'
import { PraticaStatus, PraticheLista, LoginOperatore } from '@components/Operator'
import { useOperatorAuth } from '@hooks/useOperatorAuth'
import './styles/App.css'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useOperatorAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/operatore/login', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return null

  return <>{children}</>
}

/**
 * Guardia per le route /operatore/business/*: richiede solo l'autenticazione.
 * Tutti i ruoli operatore (compreso "operatore"/"agent") possono accedere.
 */
function BusinessRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useOperatorAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated) {
      navigate('/operatore/login', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return null

  return <>{children}</>
}

function SelfServicePage() {
  const { resetForm } = useFormActions()
  const [isReady, setIsReady] = useState(false)

  const handleSessionExpired = useCallback(() => {
    resetForm()
    void clearIdentityDocumentsServerState()
    window.location.reload()
  }, [resetForm])

  const {
    sessionId,
    isLoading,
    error,
    isExpired,
    showWarning,
    secondsToExpiry,
    renewSession,
    endSession
  } = useSession({
    mode: 'self',
    onSessionExpired: handleSessionExpired
  })

  useEffect(() => {
    resetForm()
    void clearIdentityDocumentsServerState()
    const timer = setTimeout(() => {
      setIsReady(true)
    }, 500)
    return () => clearTimeout(timer)
  }, [resetForm])

  const handleContinueSession = useCallback(async () => {
    await renewSession()
  }, [renewSession])

  const handleExitSession = useCallback(async () => {
    await endSession()
    resetForm()
    void clearIdentityDocumentsServerState()
    window.location.reload()
  }, [endSession, resetForm])

  if (isLoading || !isReady) {
    return <LoadingScreen />
  }

  if (error) {
    console.error('Session error:', error)
  }

  if (isExpired) {
    return (
      <div className="app">
        <main className="app-main expired-page">
          <div className="expired-content">
            <i className="bi bi-clock-history expired-icon" />
            <h2 className="expired-title">Sessione scaduta</h2>
            <p className="expired-text">
              La tua sessione e scaduta per inattivita. Per proteggere la tua privacy (GDPR),
              i dati inseriti sono stati eliminati automaticamente.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="expired-button"
            >
              Ricomincia
            </button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="app">
      <main className="app-main">
        <FormWrapper sessionId={sessionId} />
      </main>

      <SessionTimeoutWarning
        isOpen={showWarning}
        secondsRemaining={secondsToExpiry}
        onContinue={handleContinueSession}
        onExit={handleExitSession}
        mode="self"
      />
    </div>
  )
}

function OperatorLoginPage() {
  const navigate = useNavigate()
  const { operator, isAuthenticated, isLoading, error, login } = useOperatorAuth()

  useEffect(() => {
    if (!isLoading && isAuthenticated && operator) {
      navigate(operator.home_path || '/operatore', { replace: true })
    }
  }, [isAuthenticated, isLoading, operator, navigate])

  if (isLoading) return <LoadingScreen />
  if (isAuthenticated) return null

  return (
    <LoginOperatore
      onLoginSuccess={() => {}}
      login={login}
      error={error}
      isLoading={isLoading}
    />
  )
}

function OperatorePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { operator, isLoading: authLoading } = useOperatorAuth()
  const { resetForm, setMode, setStep, setData } = useFormActions()
  const [sentSubmissionId, setSentSubmissionId] = useState<number | null>(null)
  // Usiamo useLocation (reattivo) invece di window.location.pathname per
  // accorgerci dei cambi di route business <-> domestico anche quando React
  // Router naviga lato client senza reload.
  const isBusiness = location.pathname.includes('/operatore/business')

  // Duplica pratica: se siamo arrivati qui con i dati precompilati di una
  // pratica originale, li carichiamo una sola volta nel form invece di
  // resettarlo. La nuova submission verra' creata all'invio, collegata
  // all'originale (duplicated_from_submission_id).
  const duplicatePrefillRef = useRef<{ data: Partial<FormData>; from: number } | null>(
    (location.state as { duplicatePrefill?: Partial<FormData>; duplicatedFrom?: number } | null)?.duplicatePrefill
      ? {
          data: (location.state as { duplicatePrefill: Partial<FormData> }).duplicatePrefill,
          from: (location.state as { duplicatedFrom: number }).duplicatedFrom,
        }
      : null
  )

  const handleSessionExpired = useCallback(() => {
    // operator session expired handled by ProtectedRoute
  }, [])

  const {
    sessionId,
    isLoading,
    error,
    showWarning,
    secondsToExpiry,
    renewSession,
    endSession
  } = useSession({
    mode: 'operator',
    onSessionExpired: handleSessionExpired
  })

  const { logout } = useOperatorAuth()
  const [isReady, setIsReady] = useState(false)

  // bo_ameconviene: solo lista pratiche, mai il form nuova pratica.
  useEffect(() => {
    if (authLoading || !operator?.pratiche_only) return
    navigate('/operatore/pratiche', { replace: true })
  }, [authLoading, operator?.pratiche_only, navigate])

  const initNewPratica = useCallback(async () => {
    resetForm()
    void clearIdentityDocumentsServerState()
    setMode('operator')

    // Duplica: carica i dati della pratica originale (consumati una sola
    // volta). Il documento d'identita' resta da ricaricare (stato server
    // gia' azzerato sopra).
    const dup = duplicatePrefillRef.current
    if (dup) {
      duplicatePrefillRef.current = null
      setData({ ...dup.data, duplicated_from_submission_id: dup.from })
      setStep('anagrafica')
      const timer = setTimeout(() => setIsReady(true), 700)
      return () => clearTimeout(timer)
    }

    if (isBusiness) {
      setData({ tipo_cliente: 'business' })
    }
    // Lo step 'upload' (caricamento bolletta) e' stato rimosso: per
    // l'operatore il primo step e' 'anagrafica'.
    setStep('anagrafica')
    // 700ms danno il tempo allo skeleton (con banner "Stai passando a ...")
    // di essere percepito dall'operatore al cambio percorso.
    const timer = setTimeout(() => setIsReady(true), 700)
    return () => clearTimeout(timer)
  }, [resetForm, setMode, setStep, setData, isBusiness])

  // Re-init ogni volta che cambia isBusiness (switch Domestico <-> Business).
  // Resettiamo isReady=false PRIMA del setTimeout: cosi' lo skeleton si vede
  // subito durante la transizione, evitando il flash del form precedente.
  useEffect(() => {
    setIsReady(false)
    initNewPratica()
  }, [initNewPratica])

  const handleContinueOperatorSession = useCallback(async () => {
    await renewSession()
  }, [renewSession])

  const handleExitOperatorSession = useCallback(async () => {
    await endSession()
    void clearIdentityDocumentsServerState()
    logout()
  }, [endSession, logout])

  if (authLoading || operator?.pratiche_only) {
    return <LoadingScreen />
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isReady) {
    return (
      <div className="app">
        <OperatorNavbar />
        <main className="app-main">
          <FormSkeleton switchingTo={isBusiness ? 'Business' : 'Domestico'} />
        </main>
      </div>
    )
  }

  if (error) {
    console.error('Session error:', error)
  }

  if (sentSubmissionId) {
    const basePath = isBusiness ? '/operatore/business' : '/operatore'
    return (
      <div className="app">
        <OperatorNavbar />
        <main className="app-main">
          <PraticaStatus
            submissionId={sentSubmissionId}
            onBack={() => navigate(`${basePath}/pratiche`)}
            onNewPratica={() => {
              setSentSubmissionId(null)
              initNewPratica()
            }}
          />
        </main>
        <SessionTimeoutWarning
          isOpen={showWarning}
          secondsRemaining={secondsToExpiry}
          onContinue={handleContinueOperatorSession}
          onExit={handleExitOperatorSession}
          mode="operator"
        />
      </div>
    )
  }

  return (
    <div className="app">
      <OperatorNavbar />
      <main className="app-main">
        <FormWrapper
          sessionId={sessionId}
          mode="operator"
          onOperatorSentToClient={(submissionId) => {
            setSentSubmissionId(submissionId)
          }}
        />
      </main>

      <SessionTimeoutWarning
        isOpen={showWarning}
        secondsRemaining={secondsToExpiry}
        onContinue={handleContinueOperatorSession}
        onExit={handleExitOperatorSession}
        mode="operator"
      />
    </div>
  )
}

function PraticaStatusPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isBusiness = window.location.pathname.includes('/business/')
  const basePath = isBusiness ? '/operatore/business' : '/operatore'

  if (!id || isNaN(parseInt(id))) {
    return (
      <div className="app">
        <main className="app-main error-page">
          <h2>Pratica non trovata</h2>
          <p>ID pratica non valido</p>
        </main>
      </div>
    )
  }

  return (
    <div className="app">
      <OperatorNavbar />
      <main className="app-main">
        <PraticaStatus
          submissionId={parseInt(id)}
          onBack={() => navigate(`${basePath}/pratiche`)}
          onNewPratica={() => navigate(basePath)}
        />
      </main>
    </div>
  )
}

function PraticheListaPage() {
  const navigate = useNavigate()
  const isBusiness = window.location.pathname.includes('/business')
  const basePath = isBusiness ? '/operatore/business' : '/operatore'

  return (
    <div className="app">
      <OperatorNavbar />
      <main className="app-main">
        <PraticheLista onNewPratica={() => navigate(basePath)} />
      </main>
    </div>
  )
}

function ClientSigningPage() {
  const { token } = useParams<{ token: string }>()

  if (!token) {
    return (
      <div className="app">
        <main className="app-main error-page">
          <h2>Link non valido</h2>
          <p>Il link che hai seguito non è valido o è scaduto.</p>
        </main>
      </div>
    )
  }

  return <ClientSigningWrapper token={token} />
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root: la pagina pubblica e' il login operatore. Il flusso self
        non e' attualmente in uso ma la sua codebase resta disponibile su
        /self per non perderne l'implementazione. */}
        <Route path="/" element={<Navigate to="/operatore/login" replace />} />
        <Route path="/self" element={<SelfServicePage />} />

        {/* Operator login (public) */}
        <Route path="/operatore/login" element={<OperatorLoginPage />} />

        {/* Operator area - Domestico (protected) */}
        <Route path="/operatore" element={
          <ProtectedRoute><OperatorePage /></ProtectedRoute>
        } />
        <Route path="/operatore/pratiche" element={
          <ProtectedRoute><PraticheListaPage /></ProtectedRoute>
        } />
        <Route path="/operatore/pratica/:id" element={
          <ProtectedRoute><PraticaStatusPage /></ProtectedRoute>
        } />

        {/* Operator area - Business (protected, URL-based detection).
            Solo admin / supervisor / backoffice possono accedere. */}
        <Route path="/operatore/business" element={
          <BusinessRoute><OperatorePage /></BusinessRoute>
        } />
        <Route path="/operatore/business/pratiche" element={
          <BusinessRoute><PraticheListaPage /></BusinessRoute>
        } />
        <Route path="/operatore/business/pratica/:id" element={
          <BusinessRoute><PraticaStatusPage /></BusinessRoute>
        } />

        {/* Client signing flow */}
        <Route path="/firma/:token" element={<ClientSigningPage />} />

        {/* Fallback: qualsiasi path sconosciuto torna al login operatore. */}
        <Route path="*" element={<Navigate to="/operatore/login" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
